import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, Plus, Edit2, Trash2, MessageSquare, AtSign, Image as ImageIcon,
  ToggleLeft, ToggleRight, Save, X, ChevronDown, ChevronUp,
  Loader2, Zap, TrendingUp, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TRIGGER_TYPES = [
  { value: 'dm_keyword', label: 'DM Keyword', icon: MessageSquare, description: 'Trigger when user sends DM containing keyword' },
  { value: 'comment_keyword', label: 'Comment Keyword', icon: AtSign, description: 'Trigger when user comments with keyword' },
  { value: 'story_mention', label: 'Story Mention', icon: ImageIcon, description: 'Trigger when user mentions you in story' }
];

const InstagramChatbotPage = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedFlow, setExpandedFlow] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
    triggers: []
  });

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/instagram/chatbot/flows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlows(response.data.flows || []);
    } catch (error) {
      toast.error('Failed to load chatbot flows');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingFlow(null);
    setFormData({
      name: '',
      is_active: true,
      triggers: [{
        trigger_type: 'dm_keyword',
        keyword: '',
        response_message: '',
        send_as_dm: true,
        is_active: true
      }]
    });
    setDialogOpen(true);
  };

  const openEditDialog = (flow) => {
    setEditingFlow(flow);
    setFormData({
      name: flow.name,
      is_active: flow.is_active,
      triggers: flow.triggers || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a flow name');
      return;
    }

    if (formData.triggers.length === 0) {
      toast.error('Please add at least one trigger');
      return;
    }

    // Validate triggers
    for (const trigger of formData.triggers) {
      if (trigger.trigger_type !== 'story_mention' && !trigger.keyword?.trim()) {
        toast.error('Please enter a keyword for all triggers');
        return;
      }
      if (!trigger.response_message?.trim()) {
        toast.error('Please enter a response message for all triggers');
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingFlow) {
        await axios.put(
          `${API_BASE_URL}/instagram/chatbot/flows/${editingFlow.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Chatbot flow updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/instagram/chatbot/flows`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Chatbot flow created');
      }

      setDialogOpen(false);
      loadFlows();
    } catch (error) {
      toast.error('Failed to save chatbot flow');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (flow) => {
    if (!window.confirm(`Delete chatbot flow "${flow.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/instagram/chatbot/flows/${flow.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chatbot flow deleted');
      loadFlows();
    } catch (error) {
      toast.error('Failed to delete chatbot flow');
    }
  };

  const toggleFlowActive = async (flow) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/instagram/chatbot/flows/${flow.id}`,
        { ...flow, is_active: !flow.is_active, triggers: flow.triggers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadFlows();
      toast.success(`Chatbot ${!flow.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update chatbot status');
    }
  };

  const addTrigger = () => {
    setFormData(prev => ({
      ...prev,
      triggers: [
        ...prev.triggers,
        {
          trigger_type: 'dm_keyword',
          keyword: '',
          response_message: '',
          send_as_dm: true,
          is_active: true
        }
      ]
    }));
  };

  const removeTrigger = (index) => {
    setFormData(prev => ({
      ...prev,
      triggers: prev.triggers.filter((_, i) => i !== index)
    }));
  };

  const updateTrigger = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      triggers: prev.triggers.map((t, i) => 
        i === index ? { ...t, [field]: value } : t
      )
    }));
  };

  const getTriggerIcon = (type) => {
    const trigger = TRIGGER_TYPES.find(t => t.value === type);
    return trigger?.icon || MessageSquare;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/integrations/instagram')}
              className="text-wt-text-muted hover:text-wt-text-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Bot className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-wt-text-primary">Instagram Chatbot</h1>
              <p className="text-wt-text-muted">Automate responses to DMs, comments, and story mentions</p>
            </div>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-wt-gold hover:bg-wt-gold-dark text-wt-bg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Flow
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4"
        >
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Zap className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-wt-text-primary">{flows.length}</p>
                  <p className="text-xs text-wt-text-muted">Total Flows</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <ToggleRight className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-wt-text-primary">
                    {flows.filter(f => f.is_active).length}
                  </p>
                  <p className="text-xs text-wt-text-muted">Active Flows</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-wt-text-primary">
                    {flows.reduce((acc, f) => acc + (f.stats?.messages_sent || 0), 0)}
                  </p>
                  <p className="text-xs text-wt-text-muted">Messages Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Flows List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          {flows.length === 0 ? (
            <Card className="bg-wt-surface border-wt-border">
              <CardContent className="p-8 text-center">
                <Bot className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
                <h3 className="text-lg font-medium text-wt-text-primary mb-2">No Chatbot Flows</h3>
                <p className="text-wt-text-muted mb-4">Create your first chatbot flow to automate Instagram responses</p>
                <Button onClick={openCreateDialog} className="bg-wt-gold hover:bg-wt-gold-dark text-wt-bg">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Flow
                </Button>
              </CardContent>
            </Card>
          ) : (
            flows.map((flow) => (
              <Card key={flow.id} className="bg-wt-surface border-wt-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-wt-text-primary">{flow.name}</CardTitle>
                      <Badge 
                        variant="outline" 
                        className={flow.is_active 
                          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                          : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }
                      >
                        {flow.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleFlowActive(flow)}
                        className="text-wt-text-muted hover:text-wt-text-primary"
                      >
                        {flow.is_active ? (
                          <ToggleRight className="h-5 w-5 text-green-400" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(flow)}
                        className="text-wt-text-muted hover:text-wt-text-primary"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(flow)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedFlow(expandedFlow === flow.id ? null : flow.id)}
                        className="text-wt-text-muted hover:text-wt-text-primary"
                      >
                        {expandedFlow === flow.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <CardDescription className="text-wt-text-muted">
                    {flow.triggers?.length || 0} trigger(s) • {flow.stats?.triggers_count || 0} times triggered
                  </CardDescription>
                </CardHeader>
                
                {expandedFlow === flow.id && (
                  <CardContent className="pt-0">
                    <div className="space-y-3 mt-4 border-t border-wt-border pt-4">
                      {flow.triggers?.map((trigger, index) => {
                        const TriggerIcon = getTriggerIcon(trigger.trigger_type);
                        return (
                          <div key={index} className="p-3 bg-wt-bg rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <TriggerIcon className="h-4 w-4 text-wt-gold" />
                              <span className="text-sm font-medium text-wt-text-primary">
                                {TRIGGER_TYPES.find(t => t.value === trigger.trigger_type)?.label}
                              </span>
                              {trigger.keyword && (
                                <Badge variant="outline" className="bg-wt-surface border-wt-border text-wt-text-secondary">
                                  Keyword: {trigger.keyword}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-wt-text-muted pl-6">
                              Response: {trigger.response_message}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </motion.div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary">
                {editingFlow ? 'Edit Chatbot Flow' : 'Create Chatbot Flow'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Flow Name */}
              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Flow Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Welcome Message, Product Inquiry"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-secondary">Active</Label>
                  <p className="text-xs text-wt-text-muted">Enable this chatbot flow</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>

              {/* Triggers */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-wt-text-secondary">Triggers</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addTrigger}
                    className="border-wt-border text-wt-text-secondary hover:text-wt-text-primary"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Trigger
                  </Button>
                </div>

                {formData.triggers.map((trigger, index) => (
                  <Card key={index} className="bg-wt-bg border-wt-border">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-wt-text-primary">Trigger {index + 1}</span>
                        {formData.triggers.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeTrigger(index)}
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      {/* Trigger Type */}
                      <div className="space-y-2">
                        <Label className="text-xs text-wt-text-muted">Trigger Type</Label>
                        <Select
                          value={trigger.trigger_type}
                          onValueChange={(value) => updateTrigger(index, 'trigger_type', value)}
                        >
                          <SelectTrigger className="bg-wt-surface border-wt-border text-wt-text-primary">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-wt-surface border-wt-border">
                            {TRIGGER_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value} className="text-wt-text-primary">
                                <div className="flex items-center gap-2">
                                  <type.icon className="h-4 w-4" />
                                  <div>
                                    <div>{type.label}</div>
                                    <div className="text-xs text-wt-text-muted">{type.description}</div>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Keyword (not for story mention) */}
                      {trigger.trigger_type !== 'story_mention' && (
                        <div className="space-y-2">
                          <Label className="text-xs text-wt-text-muted">Keyword</Label>
                          <Input
                            value={trigger.keyword || ''}
                            onChange={(e) => updateTrigger(index, 'keyword', e.target.value)}
                            placeholder="e.g., INFO, PRICE, HELP"
                            className="bg-wt-surface border-wt-border text-wt-text-primary"
                          />
                          <p className="text-xs text-wt-text-muted">
                            Trigger when message contains this keyword (case-insensitive)
                          </p>
                        </div>
                      )}

                      {/* Response Message */}
                      <div className="space-y-2">
                        <Label className="text-xs text-wt-text-muted">Response Message</Label>
                        <Textarea
                          value={trigger.response_message || ''}
                          onChange={(e) => updateTrigger(index, 'response_message', e.target.value)}
                          placeholder="Enter the automated response message..."
                          className="bg-wt-surface border-wt-border text-wt-text-primary min-h-[80px]"
                        />
                      </div>

                      {/* Active Toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={trigger.is_active}
                          onCheckedChange={(checked) => updateTrigger(index, 'is_active', checked)}
                        />
                        <Label className="text-xs text-wt-text-muted">Trigger active</Label>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-wt-border text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-dark text-wt-bg"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingFlow ? 'Update Flow' : 'Create Flow'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InstagramChatbotPage;
